import type { Dictionary } from './dictionaryTypes';
import { EN } from './en';

export const FR: Dictionary = {
    ...EN,
    localeLabel: 'Français',
    languages: {
        ru: 'Russe',
        en: 'Anglais',
        es: 'Espagnol',
        fr: 'Français',
        ar: 'Arabe',
        'zh-CN': 'Chinois mandarin',
        'zh-HK': 'Chinois cantonais',
    },
    common: {
        ...EN.common,
        close: 'Fermer',
        done: 'Terminé',
        settings: 'Réglages',
        preferences: 'Préférences',
        logout: 'Se déconnecter',
        copied: 'Copié!',
        backToDashboard: 'Retour au tableau de bord',
        backToChats: 'Retour aux chats',
        backToRecordings: 'Retour aux enregistrements',
        login: 'Connexion',
        forInvestors: 'Investisseurs',
        learnMore: 'En savoir plus',
        chats: 'Discussions',
        email: 'E-mail',
        openCall: 'Ouvrir l’appel',
        currentBrowser: 'Navigateur actuel',
        anotherDevice: 'Autre appareil',
        thisBrowser: 'Ce navigateur',
        moreActions: 'Plus d’actions',
    },
    appError: {
        title: 'Une erreur s’est produite',
        description: 'L’erreur a été signalée. Rechargez la page pour réessayer.',
        retry: 'Réessayer',
    },
    offline: {
        eyebrow: 'Hors ligne',
        title: 'Connexion indisponible',
        description:
            'Vous pouvez rouvrir les écrans Chattenger mis en cache, mais les nouveaux messages, appels, e-mails et synchronisations nécessitent une connexion.',
        openChats: 'Ouvrir les chats',
    },
    compatibilityRoutes: {
        ...EN.compatibilityRoutes,
        errorEyebrow: 'Route d’erreur',
        errorTitle: 'La surface d’erreur partagée est maintenant rendue via Next.js.',
        errorDescription: 'Ainsi, cette route reste disponible pendant que le reste du produit quitte l’ancienne SPA.',
        towerEyebrow: 'Route de compatibilité',
        towerTitle: 'Tower passe désormais par la coque Next.js.',
        towerDescription:
            'Le passage vers le jeu restera disponible pendant la migration de la SPA vers Next. La route est réservée et ne dépend plus de l’ancien bundle.',
        backHome: 'Retour à Chattenger',
        joinEyebrow: 'Rejoindre',
        joinInvalidTitle: 'Le lien d’invitation est invalide',
        joinInvalidDescription: 'Ce lien d’invitation est mal formé. Ouvrez-le à nouveau dans Chattenger ou demandez un nouveau lien.',
        openInChattenger: 'Ouvrir dans Chattenger',
        continueOnWeb: 'Continuer sur le web',
        joinDirectChatTitle: 'Le chat direct n’a pas pu être ouvert',
        joinDirectChatDescription:
            'L’utilisateur a été résolu, mais le chat navigateur n’a pas pu être initialisé pour le moment. Essayez de l’ouvrir dans l’application.',
        goToDashboard: 'Aller au tableau de bord',
        joinChannelEyebrow: 'Rejoindre le canal',
        joinGroupEyebrow: 'Rejoindre le groupe',
        joinUnsupportedChannelDescription:
            'Le web a résolu ce canal, mais il n’est pas encore disponible dans votre liste actuelle de chats du navigateur.',
        joinUnsupportedGroupDescription:
            'Le web a résolu ce groupe, mais il n’est pas encore disponible dans votre liste actuelle de chats du navigateur.',
        joinUnsupportedChannelFallback: 'Ouvrez ce canal dans Chattenger pour le prévisualiser ou terminer l’accès ou l’abonnement.',
        joinUnsupportedGroupFallback: 'Ouvrez ce groupe dans Chattenger pour le prévisualiser ou terminer l’accès ou l’abonnement.',
        joinNotFoundTitle: 'Invitation introuvable',
        joinNotFoundDescription:
            'Cette invitation n’a pas pu être résolue pour le compte actuel. Elle peut être invalide, privée ou ne plus être disponible.',
    },
    theme: {
        ...EN.theme,
        system: 'Système',
        light: 'Clair',
        dark: 'Sombre',
        label: 'Thème',
        hint: 'Le thème reste local à ce navigateur.',
    },
    settings: {
        ...EN.settings,
        eyebrow: 'Profil et réglages',
        title: "Préférences d'interface",
        description: 'La langue et le thème restent liés à ce navigateur pour que l’expérience soit familière à chaque retour.',
        appearanceTab: 'Apparence',
        devicesTab: 'Appareils',
        languageTab: 'Langue',
        securityTab: 'Sécurité',
        appearanceTitle: 'Mode visuel',
        appearanceCopy: 'Changez la palette instantanément sans rechargement. Le client web doit rester rapide et stable sur tous les écrans.',
        languageTitle: "Langue de l'interface",
        languageCopy: 'La localisation se met à jour immédiatement sur les surfaces clés du client web.',
        securityTitle: 'Accès de confiance',
        securityCopy:
            'Approuvez les nouveaux appareils, révoquez les anciens navigateurs et consultez les anciennes invitations d’inscription mobile depuis une session fiable.',
        profileTitle: 'Profil actuel',
        profileCopy: 'Réglages pour cet utilisateur et ce navigateur.',
        publicCopy: 'Les pages publiques conservent aussi le thème et la langue sur cet appareil.',
        photoAction: 'Photo',
        photoActionAria: 'Choisir la photo de profil',
        photoUploadFailed: "Impossible d'envoyer la photo.",
        navigationTitle: 'Sections',
        languageLabel: 'Langue',
        languageHint: 'Le russe, l’anglais, l’espagnol, le français, l’arabe, le chinois mandarin et le chinois cantonais sont pris en charge.',
        localeBadge: 'Langue',
        themeBadge: 'Thème',
        focusHighlightsTitle: 'Contours de focus',
        focusHighlightsCopy:
            'Désactivez les contours visuels de focus dans ce navigateur s’ils sont désagréables pendant une utilisation souris ou tactile classique.',
        focusHighlightsLabel: 'Contours de focus',
        focusHighlightsOn: 'Activé',
        focusHighlightsOff: 'Désactivé',
        securityLoadFailed: 'Impossible de charger la surface de sécurité.',
        securityActionFailed: 'Impossible de terminer l’action de sécurité.',
        securityDateUnavailable: 'Indisponible',
        currentDeviceCannotBeRevoked: 'Ce navigateur est votre appareil de confiance actuel et ne peut pas se révoquer lui-même.',
        devicesTitle: 'Appareils connectés',
        devicesCopy:
            'Consultez chaque session téléphone, navigateur et desktop connectée à ce compte. L’état en ligne se met à jour par socket authentifié.',
        devicesLoadFailed: 'Impossible de charger les appareils connectés.',
        devicesActionFailed: 'Impossible de mettre à jour la session de l’appareil.',
        devicesEmpty: 'Aucun appareil trouvé pour ce compte pour le moment.',
        devicesCurrent: 'Appareil actuel',
        devicesView: 'Voir',
        devicesEndSession: 'Terminer la session',
        devicesEndingSession: 'Fermeture…',
        devicesLoggedInAt: 'Connexion',
        devicesUpdatedAt: 'Dernière mise à jour',
        devicesActiveSockets: 'Sockets actifs',
        devicesUserAgent: 'User agent',
        devicesStatusOnline: 'En ligne',
        devicesStatusOffline: 'Hors ligne',
        devicesStatusSignedOut: 'Déconnecté',
        deviceInspectionBack: 'Retour aux appareils',
        deviceInspectionCopy: 'Consultez l’activité récente de cet appareil. Ouvrez un événement pour voir les métadonnées.',
        deviceInspectionEmpty: 'Aucune activité récente pour le moment.',
        deviceActivityNoMetadata: 'Aucune métadonnée',
        deviceViewRequest: 'Demander une vue en direct',
        deviceViewRequestMicrophone: 'Micro seul',
        deviceViewRequestCameraMic: 'Caméra + micro',
        deviceViewPermissionSummary: 'Autorisations cible : micro {microphone}, caméra {camera}.',
        deviceViewPermissionGranted: 'accordé',
        deviceViewPermissionPrompt: 'demandera',
        deviceViewPermissionDenied: 'bloqué',
        deviceViewPermissionUnknown: 'inconnu',
        deviceViewPermissionUnsupported: 'non pris en charge',
        deviceViewPermissionHint: 'Choisissez les options déjà accordées pour éviter une demande d’autorisation sur cet appareil.',
        deviceViewIdle: 'Aucune demande de vue active.',
        deviceViewPending: 'En attente du démarrage de la caméra et du micro sur l’autre appareil.',
        deviceViewStarting: 'Démarrage de la caméra et du micro.',
        deviceViewStreaming: 'La caméra et le micro diffusent.',
        deviceViewPermissionRequired: 'Une autorisation caméra ou micro est requise sur cet appareil.',
        deviceViewFailed: 'La vue en direct a échoué.',
        deviceViewStopped: 'La vue en direct est arrêtée.',
        deviceViewStreamingChip: '{count} appareil(s) regardent caméra et micro',
        deviceViewAccept: 'Approuver',
        deviceViewDecline: 'Refuser',
        deviceViewPromptTitle: 'Autoriser la vue de cet appareil ?',
        deviceViewPromptBody: 'Un autre appareil connecté demande à voir ce navigateur. N’approuvez que si vous l’avez demandé.',
        deviceViewRespondFailed: 'Impossible de répondre à la demande de vue de l’appareil.',
        deviceViewOverlayTitle: 'Vue en direct de l’appareil',
        deviceViewOverlayCopy: 'Utilisez ce panneau pour suivre l’état de la vue et l’activité sûre de l’appareil sélectionné.',
        deviceViewOverlayMediaPending: 'Le flux caméra/micro apparaîtra ici quand l’autre appareil commencera à diffuser.',
        deviceViewOverlayAudioActive: 'Flux audio actif',
        deviceViewOverlayLogsTitle: 'Flux d’activité',
        pendingApprovalsTitle: 'Approbations d’appareils en attente',
        pendingApprovalsCopy: 'Ces tentatives de connexion attendent l’approbation de l’un de vos appareils de confiance.',
        pendingApprovalsEmpty: 'Aucune approbation d’appareil en attente pour le moment.',
        pendingApprovalsApprove: 'Approuver',
        pendingApprovalsReject: 'Rejeter',
        pendingApprovalsKnownDevice: 'Appareil connu',
        pendingApprovalsUnknownDevice: 'Nouvel appareil',
        trustedDevicesTitle: 'Appareils de confiance',
        trustedDevicesCopy: 'Consultez les navigateurs et clients qui peuvent se connecter sans attendre une deuxième approbation.',
        trustedDevicesEmpty: 'Aucun appareil de confiance trouvé pour ce compte pour le moment.',
        trustedDevicesCurrent: 'Appareil actuel',
        trustedDevicesRevoke: 'Révoquer l’appareil',
        trustedDevicesTrustedAt: 'Approuvé',
        trustedDevicesUpdatedAt: 'Mis à jour',
        trustedDevicesStatusTrusted: 'Approuvé',
        trustedDevicesStatusPending: 'En attente',
        trustedDevicesStatusRevoked: 'Révoqué',
        invitesTitle: 'Invitations d’inscription mobile',
        invitesCopy:
            'Consultez les anciennes invitations liées au téléphone. Les nouveaux comptes doivent être créés dans l’application mobile Chattenger.',
        invitesEmpty: 'Aucune invitation active ou historique pour le moment.',
        invitesRevoke: 'Révoquer l’invitation',
        invitesExpiresAt: 'Expire',
        invitesConsumedAt: 'Utilisée',
        invitesRevokedAt: 'Révoquée',
        invitesStatusActive: 'Active',
        invitesStatusConsumed: 'Utilisée',
        invitesStatusRevoked: 'Révoquée',
    },
    landing: {
        ...EN.landing,
        eyebrow: 'Plateforme de communication sécurisée',
        title: 'Chattenger',
        description:
            'Messagerie, appels, canaux et travail dans le navigateur réunis dans un produit moderne. Chattenger est conçu comme une alternative locale, sûre et fiable aux services étrangers.',
        openLogin: 'Connexion',
        learnMore: 'Investisseurs',
        badges: ['Rapide sur mobile', 'Temps réel', 'Prêt à grandir'],
    },
    auth: {
        ...EN.auth,
        title: 'Chattenger',
        loginTitle: 'Connexion par code',
        loginDescription: 'Entrez votre numéro, confirmez le code, puis continuez dans Chattenger.',
        runtimeUnavailableTitle: 'Stand indisponible',
        runtimeUnavailableDescription: 'Le serveur ne répond pas. La connexion peut échouer pendant la reprise du stand.',
        methodLabel: 'Méthode de connexion',
        methodOtp: 'Code',
        methodQr: 'QR',
        phoneLabel: 'Téléphone',
        phonePlaceholder: 'Votre numéro',
        continue: 'Continuer',
        requesting: 'Envoi…',
        otpLabel: 'Code OTP',
        otpHint: 'Saisissez le code de vérification.',
        callOtpHint: 'Répondez à l’appel et saisissez le code entendu.',
        smsOtpHint: 'Nous allons vous envoyer un SMS. Saisissez le code.',
        verify: 'Confirmer',
        verifying: 'Vérification…',
        resendCode: 'Renvoyer le code',
        resendIn: 'Réessai dans {seconds}s',
        retryCall: 'Rappeler',
        retrySms: 'SMS encore',
        switchToCall: 'Essayer appel',
        switchToSms: 'Essayer SMS',
        changePhone: 'Changer de numéro',
        requestOtpFailed: 'Impossible de demander un OTP',
        verifyOtpFailed: 'Impossible de vérifier l’OTP',
        pendingDeviceApprovalExpiredStatus: 'Expiré',
        pendingDeviceApprovalRejectedStatus: 'Refusé',
        pendingDeviceApprovalNotFoundStatus: 'Indisponible',
        cantLogin: 'Connexion impossible ?',
        registrationEmailTitle: 'Confirmer votre email',
        registrationEmailDescription: 'Saisissez le code envoyé par email avant l’ouverture du compte.',
        registrationEmailSentTo: 'Nous avons envoyé un code à 4 chiffres à {email}.',
        trustedDeviceBootstrapEmailTitle: 'Confirmer cet appareil de confiance',
        trustedDeviceBootstrapEmailDescription: 'Saisissez le code reçu par email pour terminer la connexion sur cet appareil.',
        trustedDeviceBootstrapEmailSentTo: 'Nous avons envoyé un code à 4 chiffres à {email}.',
        mobileRegistrationRequired: 'Les nouveaux comptes doivent être créés dans l’application mobile Chattenger.',
        emailOtpLabel: 'Code email',
        emailOtpHint: 'Saisissez le code à 4 chiffres reçu par email.',
        registrationRecoveryCodesTitle: 'Sauvegarder vos codes de récupération',
        accountRecoveryTitle: 'Récupérer le compte',
        accountRecoveryDescription: 'Saisissez le code téléphone et un code de récupération pour réinitialiser les appareils.',
        recoveryCodeLabel: 'Code de récupération',
        recoveryCodePlaceholder: 'CHTG-XXXX-XXXX-XXXX-XXXX',
        recoveryCodeHint: 'Utilisez un code de secours enregistré plus tôt.',
        accountRecoveryCodesTitle: 'Nouveaux codes de récupération',
        recoveryCodesDescription: 'Ces codes ne sont affichés qu’une fois. Gardez-les privés et hors ligne.',
        recoveryCodesWriteDown: 'Notez ces codes. Ils sont le seul moyen autonome de récupérer ce compte plus tard.',
        copyRecoveryCodes: 'Copier les codes',
        emailRecoveryCodes: 'Envoyer les codes',
        recoveryCodesCopied: 'Codes copiés',
        recoveryCodesDeliveryTitle: 'Envoyer les codes de récupération',
        recoveryCodesDeliveryDescription: 'Envoyez une copie à {email}, ou gardez une copie non lue dans votre boîte Chattenger après connexion.',
        recoveryCodesVerifiedEmailLabel: 'votre email vérifié',
        recoveryCodesInboxCopyTitle: 'Créer une copie non lue dans la boîte Chattenger',
        recoveryCodesInboxCopyDescription: 'Après connexion, ouvrez Mail et gardez le message de codes non lu jusqu’à ce que vous le choisissiez.',
        sendRecoveryCodesEmail: 'Envoyer par email',
        recoveryCodesEmailSending: 'Envoi…',
        recoveryCodesInboxCopyCreating: 'Création de votre copie dans la boîte Chattenger…',
        recoveryCodesEmailSent: 'Email envoyé',
        recoveryCodesNextCountdown: 'Suivant ({seconds})',
        qrEyebrow: 'Chattenger Web',
        qrTitle: 'Connexion par QR',
        qrDescription: 'Ouvrez Chattenger sur votre téléphone et confirmez la connexion.',
        qrInstructionOne: 'Ouvrez Chattenger.',
        qrInstructionTwo: 'Allez dans Réglages.',
        qrInstructionThree: 'Choisissez la connexion web et scannez le code.',
        qrRefreshing: 'Rafraîchissement du code…',
        qrWaiting: 'En attente de confirmation depuis votre téléphone…',
        qrExpired: 'Ce code a expiré. Générez-en un nouveau.',
        qrFailed: 'Impossible de charger le QR.',
    },
    calls: {
        ...EN.calls,
        callEndedUnexpectedly: "L'appel s'est terminé de façon inattendue.",
        callParticipantUnavailable: "L'appel n'a pas pu être connecté car personne n'était disponible.",
        callConnectionUnavailable: 'Les appels sont temporairement indisponibles. Réessayez dans une minute.',
        callParticipantBusy: 'Le participant est déjà dans un autre appel.',
        callAnsweredElsewhere: 'Cet appel a été pris sur un autre appareil.',
        microphoneLabel: 'Microphone',
        videoLabel: 'Caméra',
    },
    assetViewer: {
        dialogLabel: 'Visionneuse de pièces jointes',
        thumbnailsLabel: 'Éléments',
        open: 'Ouvrir',
        download: 'Télécharger',
        downloadCurrent: 'Cet élément seulement',
        downloadAll: 'Tous les éléments',
        downloadChoiceTitle: 'Que voulez-vous télécharger ?',
        downloadChoiceDescription: 'Enregistrez seulement l’élément actuel ou lancez le téléchargement de toutes les pièces jointes de ce message.',
        previous: 'Élément précédent',
        next: 'Élément suivant',
        videoBadge: 'Vidéo',
        pdfBadge: 'PDF',
        fileBadge: 'Fichier',
    },
    assistant: {
        ...EN.assistant,
        navLabel: 'Assistant',
        eyebrow: 'Assistant',
        title: "Demander à l'assistant Chattenger",
        description:
            "Utilisez cette surface dédiée pour l'assistant. Il ne s'ouvre pas comme une discussion normale ni comme un contact recherchable.",
        inputLabel: 'Invite',
        inputPlaceholder: 'Demandez de l’aide sur Chattenger, votre espace de travail ou une tâche web claire.',
        submit: 'Demander',
        submitting: 'Réflexion…',
        pendingLabel: "L'assistant écrit…",
        responseLabel: 'Réponse',
        unavailableTitle: 'Assistant indisponible',
        unavailableCopy: "L'assistant n'est pas disponible pour le moment. Réessayez plus tard.",
        emptyCopy: "Les réponses dépendent de l'activation du fournisseur backend.",
    },
    vpnPlusShareCode: {
        ...EN.vpnPlusShareCode,
        eyebrow: 'Partage VPN+',
        title: 'Récupérer des données VPN+ partagées',
        description: 'Connectez-vous avec votre compte Chattenger existant pour ajouter cette allocation VPN+ retenue à votre solde.',
        codeLabel: 'Code',
        invalidCodeFallback: 'Code invalide',
        readyTitle: 'Prêt à récupérer',
        readyCopy: 'Ce code ne peut être utilisé qu’une fois. Chattenger le vérifiera avant de modifier votre solde VPN+.',
        redeemAction: 'Récupérer',
        redeemingAction: 'Validation…',
        redeemingTitle: 'Vérification du code',
        redeemingCopy: 'Chattenger valide ce code VPN+.',
        redeemedTitle: 'Données VPN+ ajoutées',
        redeemedCopy: 'L’allocation VPN+ partagée a été ajoutée à votre compte.',
        expiredTitle: 'Code expiré',
        expiredCopy: 'Ce code n’est plus actif. Demandez un nouveau code à l’expéditeur.',
        usedTitle: 'Code déjà utilisé',
        usedCopy: 'Ce code a déjà été récupéré et ne peut pas être réutilisé.',
        cancelledTitle: 'Code annulé',
        cancelledCopy: 'L’expéditeur a annulé ce code avant sa récupération.',
        invalidTitle: 'Code indisponible',
        invalidCopy: 'Ce code est invalide ou mal formé.',
        featureOffTitle: 'Le partage VPN+ est indisponible',
        featureOffCopy: 'La récupération de codes VPN+ n’est pas activée pour ce compte pour le moment.',
        unavailableTitle: 'Récupération impossible',
        unavailableCopy: 'Chattenger ne peut pas récupérer ce code maintenant. Réessayez plus tard.',
        copyLink: 'Copier le lien',
        copyLinkSuccess: 'Lien VPN+ copié.',
        copyLinkFailed: 'Impossible de copier ce lien.',
    },
    dashboard: {
        ...EN.dashboard,
        jumpToLatest: 'Aller aux derniers',
        messageMediaQueuedImage: 'Image en file',
        messageMediaQueuedVideo: 'Vidéo en file',
        messageMediaVideoBadge: 'Vidéo',
        messageMediaImageFallback: 'Image {index}',
        messageMediaVideoFallback: 'Vidéo {index}',
        messageMediaThumbnailFallback: 'Aperçu',
        circleVideoExpandAria: 'Développer la vidéo circulaire',
        circleVideoResumeAria: 'Reprendre la vidéo circulaire',
        circleVideoCollapseAria: 'Réduire la vidéo circulaire',
        circleVideoPlaceholder: 'L’aperçu de la vidéo circulaire se charge',
        circleVideoPaused: 'En pause',
        circleVideoLivePreview: 'Aperçu en direct',
        circleVideoUnmute: 'Activer le son',
        circleVideoMute: 'Couper le son',
        circleVideoSeekHandleAria: 'Déplacer la lecture de la vidéo circulaire',
    },
    admin: {
        ...EN.admin,
        nav: {
            ...EN.admin.nav,
            mermaid: 'Éditeur Mermaid',
        },
        mermaid: {
            canvasLabel: 'Espace de travail du diagramme',
            editorTab: 'Éditeur',
            errorLink: 'Le lien du diagramme est invalide ou dépasse la limite sûre.',
            errorSyntax: 'Le diagramme n’a pas pu être rendu. Corrigez la syntaxe Mermaid puis réessayez.',
            fit: 'Ajuster le diagramme',
            loading: 'Chargement de l’éditeur…',
            pageDescription:
                'Modifiez un diagramme privé dans le navigateur et partagez son état URL borné sans enregistrer la source dans Chattenger.',
            pageEyebrow: 'Admin / Mermaid',
            pageTitle: 'Éditeur Mermaid',
            previewTab: 'Aperçu',
            ready: 'Le diagramme est prêt',
            rendering: 'Rendu du diagramme…',
            reset: 'Réinitialiser la vue',
            share: 'Copier le lien',
            shareCopied: 'Lien copié',
            shareUnavailable: 'La copie du lien est indisponible dans ce navigateur.',
            sourceLabel: 'Source Mermaid',
            zoomIn: 'Zoom avant',
            zoomOut: 'Zoom arrière',
        },
    },
    recordings: {
        eyebrow: 'Enregistrements',
        title: 'Vos sessions owner-private d’enregistrement de salle sur tous les appareils.',
        listTitle: 'Enregistrements',
        noRecordingsEyebrow: 'Aucun enregistrement pour le moment',
        noRecordingsTitle: 'Vos sessions d’enregistrement apparaîtront ici.',
        noRecordingsCopy: 'Lancez un enregistrement d’appel de groupe sur le web ou le mobile, et chaque session privée apparaîtra ici séparément.',
        detailEyebrow: 'Détail de l’enregistrement',
        playback: 'Lecture',
        downloadMp4: 'Télécharger le MP4',
        sceneManifest: 'Manifeste de scène',
        newBadge: 'Nouveau',
        playbackPending: 'Cet enregistrement attend encore la fin de l’appel ou le paquet final avant d’être lisible.',
        playbackFailed: 'Cet enregistrement a échoué pendant la préparation de l’artefact.',
        previewLabel: 'Aperçu',
        roomLabel: 'Salle #{roomId}',
        chatFallbackLabel: 'Chat {chatId}',
        createdLabel: 'Créé',
        roomStateLabel: 'État de la salle',
        roomStateEnded: 'Appel terminé',
        roomStateRecordingLive: 'Enregistrement en cours',
        roomStateNoActive: 'Aucun enregistrement actif',
        roomActivityLabel: 'Activité de la salle',
        roomActivityCount: 'Sessions actives : {count}',
        roomActivityIdle: 'Inactive',
        artifactLabel: 'Artefact',
        durationLabel: 'Durée',
        durationSeconds: '{seconds}s',
        playbackReadyLabel: 'Prêt à ouvrir',
        playbackPendingLabel: 'Pas encore prêt',
        details: 'Détails',
        searchRecordingsLabel: 'Rechercher des enregistrements',
        filterAll: 'Tous',
        filterReady: 'Prêts',
        filterActive: 'Actifs',
        sortRecent: 'Récents',
        sortChat: 'Chat',
        sortStatus: 'Statut',
        paginationPrevious: 'Précédent',
        paginationNext: 'Suivant',
        paginationStatus: 'Page {current} sur {total}',
        lastStartedLabel: 'Dernier démarrage',
        pausedAtLabel: 'Mis en pause à',
        pendingRoomEndLabel: 'Fin de salle en attente',
        sealedAtLabel: 'Scellé à',
        artifactReadyLabel: 'Artefact prêt',
        artifactFailedLabel: 'Échec de l’artefact',
        sessionIdLabel: 'Id de session',
        ownerDeviceLabel: 'Appareil du propriétaire',
        ownerOtherDeviceLabel: 'Autre appareil #{deviceId}',
        artifactStatusLabel: 'Statut de l’artefact',
        artifactDurationLabel: 'Durée de l’artefact',
        artifactSizeLabel: 'Taille de l’artefact',
        sizeMegabytes: '{size} MB',
        artifactErrorLabel: 'Erreur de l’artefact',
        recordedIntervalsLabel: 'Intervalles enregistrés',
        timeRangePattern: '{start} → {end}',
        ownerPrivateRecordingTitle: 'Enregistrement owner-private',
        copiedLabel: 'Copié',
        copyFailedLabel: 'Échec de copie',
        copyDetailsLabel: 'Copier les détails',
        playPlaybackLabel: 'Lire',
        pausePlaybackLabel: 'Pause',
        seekPlaybackLabel: 'Déplacer la lecture',
        rewindTenSecondsLabel: 'Reculer de 10 secondes',
        forwardTenSecondsLabel: 'Avancer de 10 secondes',
        enterFullscreenLabel: 'Ouvrir en plein écran',
        exitFullscreenLabel: 'Quitter le plein écran',
        processingTimelineLabel: 'Chronologie de traitement',
        summaryRecordingLine: 'Enregistrement #{id}',
        summaryRoomLine: 'Salle #{roomId}',
        summaryStatusLine: 'Statut : {status}',
        summaryArtifactLine: 'Artefact : {artifact}',
    },
};
